import { Box, ButtonBase, Typography, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [display, setDisplay] = useState('small');
  const { getData } = useAuth();

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const response = await getData("proccess.php", "Annousement_V2");
        
        if (response.success && Array.isArray(response.data)) {
          // Filter only active announcements
          const activeAnnouncements = response.data.filter((announcement) => {
            return announcement.is_active === '1' || announcement.is_active === 1;
          });
          
          setAnnouncements(activeAnnouncements);
        } else {
          console.error("Announcement response error:", response.message || "No data");
          setAnnouncements([]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load announcements. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, [getData]);

  // Don't render if loading or error or no announcements or manually closed
  if (loading) return null;
  if (error) return null;
  if (announcements.length === 0) return null;

  return (
    <Box sx={{
      position: 'absolute',
      bottom: '-60px',
      right: 'auto', // Changed to right side to avoid conflicts
      left: '0',
      zIndex: 1, // Higher z-index to ensure visibility
      maxWidth: '350px',
      width: `100%`,
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      overflow: 'hidden',
      border: '1px solid #e0e0e0',
      animation: 'slideIn 0.3s ease-out'
    }}>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      
      {announcements.map((announcement, index) => (
        display === 'small' ? (
          <Box key={announcement.id || index} sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '0 10px'
          }}
          onClick={() => setDisplay('big')}
          >
            <IconButton
              onClick={() => setDisplay('small')}
              sx={{
                position: 'absolute',
                top: '10px',
                right: '8px',
                zIndex: 1,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)'
                },
                width: '28px',
                height: '28px'
              }}
              size="small"
            >
              <OpenInFullIcon sx={{ fontSize: '16px' }} />
            </IconButton>

            {/* Image */}
            {announcement.image && (
              <Box sx={{ position: 'relative' }}>
                <img 
                  src={`https://mindsinaction.com.na/assets/media/pictures/${announcement.image}`} 
                  alt={announcement.image || 'Announcement'}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '50px',
                    maxWidth: '50px',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}

            <Box sx={{ padding: '16px', display: {xs: 'block', md: 'block'} }}>
              {/* Content */}
              {announcement.link_one_lable && (
                <Typography 
                  sx={{ 
                    color: '#333',
                    fontSize: '1rem',
                    fontWeight: '700',
                    lineHeight: 1.5,
                    pr: '20px' // Space for close button
                  }}
                >
                  {announcement.link_one_lable}
                </Typography>
              )}
            </Box>

            
          </Box>
        ): (
          <Box key={announcement.id || index} sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setDisplay('small')}
          >

            <IconButton
              onClick={() => setDisplay('small')}
              sx={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 1,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)'
                },
                width: '28px',
                height: '28px'
              }}
              size="small"
            >
              <CloseFullscreenIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            
            {/* Image */}
            {announcement.image && (
              <Box sx={{ position: 'relative' }}>
                <img 
                  src={`https://mindsinaction.com.na/assets/media/pictures/${announcement.image}`} 
                  alt={announcement.image || 'Announcement'}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '200px',
                    maxWidth: '200px',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
            
            <Box sx={{ padding: '16px' }}>
              {/* Content */}
              {announcement.content && (
                <Typography 
                  sx={{ 
                    color: '#333',
                    mb: 2,
                    fontSize: '14px',
                    lineHeight: 1.5,
                    pr: '20px' // Space for close button
                  }}
                >
                  {announcement.content}
                </Typography>
              )}
              
              {/* Links */}
              {(announcement.link_one || announcement.link_two) && (
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-start', mt: 1 }}>
                  {announcement.link_one && announcement.link_one_lable && (
                    <ButtonBase 
                      component="a"
                      href={announcement.link_one}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#1565c0'
                        }
                      }}
                    >
                      {announcement.link_one_lable}
                    </ButtonBase>
                  )}
                  
                  {announcement.link_two && announcement.link_two_lable && (
                    <ButtonBase 
                      component="a"
                      href={announcement.link_two}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        backgroundColor: '#dc004e',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#c51162'
                        }
                      }}
                    >
                      {announcement.link_two_lable}
                    </ButtonBase>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )
        
      ))}
    </Box>
  );
};

export default Announcement;